import { useState } from "react";
import { Play, Instagram } from "lucide-react";
import { SHOW_GUESTS, SOCIALS, IMAGES } from "../data/mock";
import { Reveal } from "./Reveal";
import { Scribble } from "./Scribble";

export const ShowSection = () => {
  const [playing, setPlaying] = useState(false);
  const videoId = IMAGES.showVideoId;
  const start = IMAGES.showVideoStart || 0;
  const thumb = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  return (
    <section
      id="show"
      data-testid="show-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-20"
      style={{
        background: "var(--brand-cream-soft)",
        borderTop: "1px solid var(--brand-line)",
        borderBottom: "1px solid var(--brand-line)",
      }}
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <Reveal>
          <h2
            className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.04]"
            style={{ color: "var(--brand-ink)" }}
            data-testid="show-heading"
          >
            <Scribble>podcast</Scribble>
            <br className="hidden md:block" />
            <span className="md:hidden"> </span>
            <span style={{ color: "var(--brand-accent)" }}>
              Hangout with Hunar
            </span>
          </h2>

          <div
            className="mt-8 text-lg leading-relaxed"
            style={{ color: "var(--brand-muted)" }}
            data-testid="show-description"
          >
            <p>
              Hangout with Hunar is a long-form conversation series where I sit
              down with genuinely interesting people and talk about
              entrepreneurship, business, life, health and everything in
              between. The idea is simple: we are hanging out. You, the viewer,
              get to hang out with us, in a light and easy way.
            </p>
          </div>

          <div className="mt-9">
            <p
              className="text-[12px] font-semibold uppercase tracking-[0.28em] mb-4"
              style={{ color: "var(--brand-accent)" }}
            >
              Guests include
            </p>
            <div className="flex flex-wrap gap-2.5">
              {SHOW_GUESTS.map((g) => (
                <span
                  key={g}
                  className="text-sm font-semibold px-4 py-2 rounded-full"
                  style={{
                    background: "var(--brand-cream)",
                    border: "1px solid var(--brand-line)",
                    color: "var(--brand-ink-soft)",
                  }}
                >
                  {g}
                </span>
              ))}
            </div>
            <p
              className="mt-5 text-base"
              style={{ color: "var(--brand-muted)" }}
            >
              And you can be a guest too. If you have a story worth hanging out
              over, reach out.
            </p>
          </div>

          <a
            href={SOCIALS.instagramDM}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="show-guest-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-full btn-primary font-semibold px-7 py-3.5"
          >
            <Instagram size={17} strokeWidth={2} />
            Want to be a guest? Get in touch
          </a>
        </Reveal>

        <Reveal delay={0.12}>
          <div
            data-testid="show-video-player"
            className="relative aspect-video rounded-[24px] overflow-hidden group"
            style={{ border: "1px solid var(--brand-line)" }}
          >
            {playing ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&start=${start}&rel=0&modestbranding=1`}
                title="Hangout with Hunar, episode one"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            ) : (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                data-testid="show-play-button"
                aria-label="Play Hangout with Hunar, episode one"
                className="absolute inset-0 w-full h-full cursor-pointer group"
              >
                <img
                  src={thumb}
                  alt="Hangout with Hunar, episode one"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  onError={(e) => {
                    e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                  }}
                />
                <div className="absolute inset-0 bg-[#0a0a0a]/35 group-hover:bg-[#0a0a0a]/25 transition-colors duration-300" />
                <span
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70px] h-[70px] md:w-20 md:h-20 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300"
                  style={{ background: "var(--brand-cream)" }}
                >
                  <Play
                    size={28}
                    strokeWidth={1.5}
                    className="ml-1"
                    fill="currentColor"
                    style={{ color: "var(--brand-accent)" }}
                  />
                </span>
                <span
                  className="absolute bottom-4 left-4 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.2em] backdrop-blur-md text-white"
                  style={{ background: "rgba(0,0,0,0.55)" }}
                >
                  Episode 01, Watch on YouTube
                </span>
              </button>
            )}
          </div>
          <div className="mt-6" data-testid="show-video-caption">
            <p
              className="text-[12px] font-semibold uppercase tracking-[0.28em] mb-2"
              style={{ color: "var(--brand-accent)" }}
            >
              About this video
            </p>
            <p
              className="text-base leading-relaxed"
              style={{ color: "var(--brand-muted)" }}
            >
              This is our very first video, where I tell you what Hangout with
              Hunar is all going to be about: the kind of guests you'll meet
              and why I believe these conversations are worth your time.
              Consider it the trailer for everything that's coming.
            </p>
            <a
              href={`https://www.youtube.com/watch?v=${videoId}&t=${start}s`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="show-video-youtube-link"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80 transition-opacity"
              style={{ color: "var(--brand-accent)" }}
            >
              Watch on YouTube
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
